import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useClientForms } from '../../api/queries';
import {
  EmptyState,
  ErrorState,
  PageHeader,
  ProgressBar,
  Spinner,
  StatusBadge,
} from '../../components/ui';
import { useToast } from '../../components/Toast';
import { errorMessage } from '../../lib/api';
import type { ClientFormStatus } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import { CreateLinkForm } from './CreateLinkForm';

const STATUS_FILTERS: { value: '' | ClientFormStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'submitted', label: 'Submitted' },
];

export function LinksPage() {
  const [params, setParams] = useSearchParams();
  const newFor = params.get('new');
  const [showCreate, setShowCreate] = useState(newFor !== null);
  const [status, setStatus] = useState<'' | ClientFormStatus>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const toast = useToast();

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const {
    data: forms,
    isPending,
    error,
  } = useClientForms({ status: status || undefined, q: q || undefined });

  function closeCreate() {
    setShowCreate(false);
    if (newFor !== null) setParams({}, { replace: true });
  }

  async function copy(url: string) {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
  }

  return (
    <>
      <PageHeader
        title="Client links"
        subtitle="Create a personal link for a client, send it, and follow their progress."
        actions={
          !showCreate && (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New client link
            </button>
          )
        }
      />

      {showCreate && (
        <CreateLinkForm defaultTemplateId={newFor || undefined} onCancel={closeCreate} />
      )}

      <div className="toolbar">
        <input
          className="input search"
          type="search"
          placeholder="Search by client name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="segmented" role="tablist">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              role="tab"
              aria-selected={status === f.value}
              className={status === f.value ? 'active' : ''}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isPending && <Spinner />}
      {error && <ErrorState message={errorMessage(error)} />}
      {forms?.length === 0 && (
        <EmptyState title={q || status ? 'No matching links' : 'No client links yet'}>
          {!q && !status && (
            <p className="muted">Create your first link to send a form to a client.</p>
          )}
        </EmptyState>
      )}

      {forms && forms.length > 0 && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th className="hide-sm">Form</th>
                <th>Status</th>
                <th className="hide-sm">Progress</th>
                <th className="hide-sm">Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id}>
                  <td>
                    <Link to={`/admin/links/${f.id}`} className="strong">
                      {f.clientName}
                    </Link>
                    {f.clientEmail && <div className="muted small">{f.clientEmail}</div>}
                  </td>
                  <td className="hide-sm">{f.templateName}</td>
                  <td>
                    <StatusBadge status={f.status} isActive={f.isActive} />
                  </td>
                  <td className="hide-sm progress-cell">
                    <ProgressBar value={f.answeredCount} max={f.questionCount} />
                    <span className="muted small">
                      {f.answeredCount}/{f.questionCount}
                    </span>
                  </td>
                  <td className="hide-sm muted small">{formatDate(f.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => void copy(f.url)}>
                      Copy link
                    </button>
                    <Link className="btn btn-secondary btn-sm" to={`/admin/links/${f.id}`}>
                      {f.status === 'submitted' ? 'View answers' : 'Open'}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
